import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useCallback, useEffect, useState } from 'react';
import { useField } from 'react-final-form';

import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { EVENTS_BLOCK_ID } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmEventTypesSelectOptions, getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { KNM_CHECKLIST_INFO } from 'CorePackage/MainModule/routes/routes';
import { IKnmErknmFormRescheduling } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRescheduling';
import { IKnmErknmFormReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormReason';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { IInteractionCategorySelectOption } from '../../CorePackage/CatalogsModule/interfaces/IInteractionCategorySelectOption';
import { ErknmEventItem } from './components/ErknmEventItem';
import { ErknmFieldMark } from '../MainModule/components/ErknmFieldMark';
import { ErknmApproveRemarkAddButton } from '../ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';
import { ErknmApproveRemarkSectionCodeEnum } from '../ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { ErknmApproveRemark } from '../ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { useIsEventsEnabled } from '../MainModule/fieldsAvailability/erknmEventsAvailability';
import { ErknmEventsError } from './ErknmEventsError';
import styles from './ErknmEvents.module.scss';

interface IErknmEventsProps {
  events: IKnmErknmFormEvent[] | undefined;
  interactionCategoryOptions: IInteractionCategorySelectOption[] | undefined;
  reasonFieldValue: IKnmErknmFormReason | undefined;
  rescheduling?: IKnmErknmFormRescheduling[] | null | undefined;
}

interface IErknmEventsStoreProps {
  formRef: IKnmFormRef | undefined;
  knmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
}

const ErknmEventsWithoutStore = ({
  events,
  interactionCategoryOptions,
  reasonFieldValue,
  rescheduling,
  formRef,
  knmEventTypesSelectOptions,
}: IErknmEventsProps & IErknmEventsStoreProps) => {
  const isEnabled = useIsEventsEnabled();
  const push = formRef?.current.mutators.push;
  const [uniqKnmEventTypesSelectOptions, setUniqKnmEventTypesSelectOptions] = useState<
    IDictionaryEventTypeSelectOption[] | undefined
  >(knmEventTypesSelectOptions);
  const isEventDeleted = useField<boolean>('isEventDeleted').input.value ?? false;
  const isImpossibilityOfPrevious = reasonFieldValue?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;
  const hasEventsFromLink = events?.some(event => event.isFilledFromLink === true);
  // действия переносятся из ссылки: пока ссылка прикреплена, ручное добавление недоступно —
  // даже если все перенесённые действия удалены (заполнение только через переприкрепление ссылки)
  const hasLinkFilledEvents = hasEventsFromLink || (!!reasonFieldValue?.links?.length && isEventDeleted);

  const addEvent = useCallback(() => {
    if (isEnabled && !hasLinkFilledEvents && push) {
      const addingEvent: IKnmErknmFormEvent = {
        guid: undefined,
        type: undefined,
        startDate: undefined,
        stopDate: undefined,
        dateCreate: undefined,
        interactionCategory: undefined,
        listId: uuidv4(),
        isNew: true,
        submittedDocuments: undefined,
      };

      push('eventsErknm', addingEvent);
    }
  }, [formRef?.current.getFieldState('eventsErknm'), push, isEnabled, knmEventTypesSelectOptions]);

  useEffect(() => {
    const eventTypes = formRef?.current.getFieldState('eventsErknm')?.value?.map(x => x.type);

    if (!uniqKnmEventTypesSelectOptions) {
      if (eventTypes) {
        setUniqKnmEventTypesSelectOptions(
          knmEventTypesSelectOptions?.filter(x => !eventTypes.find(type => type?.title === x.title)),
        );
      } else {
        setUniqKnmEventTypesSelectOptions(knmEventTypesSelectOptions);
      }
    }
  }, [uniqKnmEventTypesSelectOptions, knmEventTypesSelectOptions, formRef?.current.getFieldState('eventsErknm')]);

  const getInteractionCategoryOptions = () => {
    if (formRef?.current.getFieldState('interactionCategory')?.value?.label?.includes('1')) {
      return interactionCategoryOptions?.filter(x => x.label?.includes('1'));
    }
    if (formRef?.current.getFieldState('interactionCategory')?.value?.label?.includes('3')) {
      return interactionCategoryOptions?.filter(x => x.label?.includes('3'));
    }

    return interactionCategoryOptions;
  };

  return (
    <FieldBlock
      label={
        <>
          Перечень действий, осуществляемых в рамках КНМ
          <ErknmFieldMark fieldName="eventsErknm" />
        </>
      }
      id={KNM_CHECKLIST_INFO}
      margin="bottom"
      button={
        <>
          {!isImpossibilityOfPrevious && !hasLinkFilledEvents && (
            <Button
              className="shared-btn-add"
              text="Добавить"
              variant="link"
              color="primary"
              type="button"
              id="erknmEventsAddButton"
              disabled={!isEnabled}
              onClick={addEvent}
            />
          )}
          <ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.EVENTS} />
        </>
      }
      labelId={EVENTS_BLOCK_ID}
      fillInProcess={<ErknmFillInProcessMessage name="eventsErknm" />}
    >
      <ErknmEventsError />
      <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.EVENTS} />
      <ErknmEventItem
        interactionCategoryOptions={getInteractionCategoryOptions()}
        name="eventsErknm"
        watchOnly={!isEnabled}
        rescheduling={rescheduling}
        uniqKnmEventTypesSelectOptions={uniqKnmEventTypesSelectOptions}
        setUniqKnmEventTypesSelectOptions={setUniqKnmEventTypesSelectOptions}
        knmEventTypesSelectOptions={knmEventTypesSelectOptions}
      />
      {(isImpossibilityOfPrevious || hasLinkFilledEvents) && isEventDeleted && (
        <div className={styles.Yellow}>
          Для добавления сведений в раздел необходимо удалить и повторно добавить ссылку на паспорт
        </div>
      )}
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmEventsStoreProps => ({
  formRef: getKnmFormRef(state),
  knmEventTypesSelectOptions: getKnmEventTypesSelectOptions(state),
});

export const ErknmEvents = connect(mapStateToProps)(ErknmEventsWithoutStore);
